import { BannerModerationStatus } from 'utils/constants/banner';
import {
    CampaignDeviceType,
    CampaignGender,
    CampaignLanguage,
    CampaignProductType,
    CampaignSource,
    CampaignSubSource,
    CampaignType,
} from 'utils/constants/campaign';
import { CampaignArchiveReason, CampaignPauseReason, CampaignStatus } from 'utils/constants/dashboard';

import { BudgetStates, CampaignStates, ModerationStates, ProductTypes } from 'components/Dashboard/Filters/consts';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import type { SalaryRange } from 'store/models/createCampaign';

export interface Statistics {
    averagePricePerClickInCents?: number;
    amount: number;
    clicks: number;
    cost: number;
    ctr: number;
    maxClickDate: string | null;
    minClickDate: string | null;
    shows: number;
}

export interface State {
    moderationReason: string;
    source: CampaignSource;
    status: BannerModerationStatus;
}

export interface Banner {
    createdAt: string;
    id: number;
    imageUrl: string;
    states: State[];
    statistics: Statistics;
    text: string;
    title: string;
    url: string;
    vacancyId: number;
    enabled: boolean;
    moderationStatus: BannerModerationStatus;
    hasVideo: boolean;
    bannedAsAdvertisement: boolean;
    bannedForAllSources: boolean;
}

export interface CampaignAreas {
    include: string[];
    exclude: string[];
}

export interface CampaignVacancy {
    name: string;
    url: string;
    vacancyId: string;
}

export interface AppliedFilters {
    productTypes?: string[];
    campaignStatuses?: string[];
    budgetStatuses?: string[];
    moderationStatuses?: string[];
    areas?: string[];
    professionalRoles?: string[];
    createdByUserIds?: string[];
}

export interface FiltersList {
    productTypes?: ProductTypes[];
    campaignStatuses?: CampaignStates[];
    budgetStatuses?: BudgetStates[];
    moderationStatuses?: ModerationStates[];
    areas?: Record<string, string>;
    professionalRoles?: Record<string, string>;
    createdByUserIds?: Record<string, string>;
}

export type FilterName = keyof FiltersList;
export type FilterValues = FiltersList[FilterName];

export type FrontendStatusType = CampaignStatus | CampaignPauseReason;

export type CheckedCampaignIds = Record<number, boolean>;

export interface Campaign {
    age: number[];
    allBanners: Banner[];
    archivedVacancy: boolean; // поле не используется
    areas: CampaignAreas;
    archiveReason: CampaignArchiveReason | null;
    budget: number | null;
    budgetDaily: number | null;
    countBanners: number;
    createdAt: string;
    deviceType: CampaignDeviceType;
    frontendStatus: FrontendStatusType;
    id: number;
    language: CampaignLanguage[];
    name: string;
    productType: CampaignProductType;
    professionalRoles: string[];
    salaryRange: SalaryRange | null;
    sex: CampaignGender;
    sources: CampaignSubSource[];
    statistics: Statistics;
    vacancyId: number | null;
    vacancy: CampaignVacancy | null;
    startAt: string | null;
    endAt: string | null;
    advertisingServiceType: CampaignType;
}

export enum CampaignTabStatus {
    Active = 'active',
    Off = 'off',
}

export enum CampaignLoadState {
    Loading = 'loading',
    Success = 'success',
    Disabled = 'disabled',
}

export interface DashboardFilters {
    productType: string[];
}

export const INITIAL_FILTERS_STATE = {
    productType: [],
};

// ----------------------- actions -------------------------------------
const SET_CHECKED_CAMPAIGN_IDS = 'SET_CHECKED_CAMPAIGN_IDS';
const SET_CAMPAIGNS = 'SET_CAMPAIGNS';
const SET_IS_LOAD_CAMPAIGNS = 'SET_IS_LOAD_CAMPAIGNS';
const SET_BANNERS = 'SET_BANNERS';
const SET_SAVING_BANNER = 'SET_SAVING_BANNER';
const SET_CAMPAIGN_SEARCH_VALUE = 'SET_CAMPAIGN_SEARCH_VALUE';
const SET_IS_CAMPAIGN_SEARCH_LOADING = 'SET_IS_CAMPAIGN_SEARCH_LOADING';
const SET_IS_CAMPAIGN_SEARCH_UPLOADED = 'SET_IS_CAMPAIGN_SEARCH_UPLOADED';
const SET_FILTERS_LIST = 'SET_FILTERS_LIST';
const SET_IS_LOADING_FILTERS_LIST = 'SET_IS_LOADING_FILTERS_LIST';
const SET_APPLIED_FILTERS = 'SET_SELECTED_FILTERS';
const SET_APPLIED_FILTERS_COUNT = 'SET_APPLIED_FILTERS_COUNT';

interface PayloadTypes {
    [SET_CAMPAIGNS]: Campaign[];
    [SET_CHECKED_CAMPAIGN_IDS]: CheckedCampaignIds;
    [SET_IS_LOAD_CAMPAIGNS]: CampaignLoadState;
    [SET_BANNERS]: Campaign[];
    [SET_SAVING_BANNER]: boolean;
    [SET_CAMPAIGN_SEARCH_VALUE]: string;
    [SET_IS_CAMPAIGN_SEARCH_LOADING]: boolean;
    [SET_IS_CAMPAIGN_SEARCH_UPLOADED]: boolean;
    [SET_FILTERS_LIST]: FiltersList;
    [SET_IS_LOADING_FILTERS_LIST]: boolean;
    [SET_APPLIED_FILTERS]: AppliedFilters;
    [SET_APPLIED_FILTERS_COUNT]: number;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setCampaigns = ActionCreator(SET_CAMPAIGNS);
export const setCheckedCampaignIds = ActionCreator(SET_CHECKED_CAMPAIGN_IDS);
export const setBanners = ActionCreator(SET_BANNERS);
export const setLoadCampaigns = ActionCreator(SET_IS_LOAD_CAMPAIGNS);
export const setBannerSaving = ActionCreator(SET_SAVING_BANNER);
export const setCampaignSearchValue = ActionCreator(SET_CAMPAIGN_SEARCH_VALUE);
export const setIsCampaignSearchLoading = ActionCreator(SET_IS_CAMPAIGN_SEARCH_LOADING);
export const setIsCampaignSearchUploaded = ActionCreator(SET_IS_CAMPAIGN_SEARCH_UPLOADED);
export const setFiltersList = ActionCreator(SET_FILTERS_LIST);
export const setIsLoadingFiltersList = ActionCreator(SET_IS_LOADING_FILTERS_LIST);
export const setAppliedFilters = ActionCreator(SET_APPLIED_FILTERS);
export const setAppliedFiltersCount = ActionCreator(SET_APPLIED_FILTERS_COUNT);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface DashboardState {
    checkedCampaignIds: CheckedCampaignIds;
    isLoad: CampaignLoadState;
    campaigns: Campaign[];
    isBannerSaving: boolean;
    campaignSearchValue: string;
    filtersList: FiltersList;
    isLoadingFiltersList: boolean;
    appliedFilters: AppliedFilters;
    appliedFiltersCount: number;
    isCampaignSearchLoading: boolean;
    isCampaignSearchUploaded: boolean;
}

export const INITIAL_APPLIED_FILTERS_STATE = {
    productTypes: [],
    campaignStatuses: [],
    budgetStatuses: [],
    moderationStatuses: [],
    areas: [],
    professionalRoles: [],
    createdByUserIds: [],
};

export const DASHBOARD_INITIAL_STATE: DashboardState = {
    checkedCampaignIds: {},
    isLoad: CampaignLoadState.Success,
    campaigns: [],
    isBannerSaving: false,
    campaignSearchValue: '',
    isCampaignSearchLoading: false,
    isCampaignSearchUploaded: false,
    filtersList: {
        productTypes: [],
        campaignStatuses: [],
        budgetStatuses: [],
        moderationStatuses: [],
        areas: {},
        professionalRoles: {},
        createdByUserIds: {},
    },
    isLoadingFiltersList: false,
    appliedFilters: { ...INITIAL_APPLIED_FILTERS_STATE },
    appliedFiltersCount: 0,
};

export default createReducer<DashboardState, PayloadTypes>(DASHBOARD_INITIAL_STATE, {
    [SET_CAMPAIGNS]: (state, { payload }) => ({
        ...state,
        campaigns: payload,
    }),
    [SET_CHECKED_CAMPAIGN_IDS]: (state, { payload }) => ({
        ...state,
        checkedCampaignIds: payload,
    }),
    [SET_IS_LOAD_CAMPAIGNS]: (state, { payload }) => ({
        ...state,
        isLoad: payload,
    }),
    [SET_BANNERS]: (state, { payload }) => ({
        ...state,
        campaigns: payload,
    }),
    [SET_SAVING_BANNER]: (state, { payload }) => ({
        ...state,
        isBannerSaving: payload,
    }),
    [SET_CAMPAIGN_SEARCH_VALUE]: (state, { payload }) => ({
        ...state,
        campaignSearchValue: payload,
    }),
    [SET_IS_CAMPAIGN_SEARCH_LOADING]: (state, { payload }) => ({
        ...state,
        isCampaignSearchLoading: payload,
    }),
    [SET_IS_CAMPAIGN_SEARCH_UPLOADED]: (state, { payload }) => ({
        ...state,
        isCampaignSearchUploaded: payload,
    }),
    [SET_FILTERS_LIST]: (state, { payload }) => ({
        ...state,
        filtersList: payload,
    }),
    [SET_IS_LOADING_FILTERS_LIST]: (state, { payload }) => ({
        ...state,
        isLoadingFiltersList: payload,
    }),
    [SET_APPLIED_FILTERS]: (state, { payload }) => ({
        ...state,
        appliedFilters: payload,
    }),
    [SET_APPLIED_FILTERS_COUNT]: (state, { payload }) => ({
        ...state,
        appliedFiltersCount: payload,
    }),
});
// ----------------------- /reducer --------------------------------------
